import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ClientConfig } from '@models/ClientConfig';
import { UserProfile } from '@models/UserProfile';
import { OktaAuthStateService } from '@okta/okta-angular';
import {
  Observable,
  catchError,
  combineLatest,
  distinctUntilChanged,
  map,
  mergeMap,
  of,
  shareReplay,
  startWith,
  throwError,
} from 'rxjs';
import { environment } from 'src/environments/environment';
import { handleHttpError, pollAndCache } from '../rxjs-operators';

export interface Config {
  configId: string;
  config: any;
}

@Injectable({
  providedIn: 'root',
})
export class ConfigService {
  public CurrentConfig$: Observable<ClientConfig>;
  public CurrentProfile$: Observable<UserProfile>;
  constructor(
    private http: HttpClient,
    private ss: OktaAuthStateService,
  ) {
    const config$ = this.http.get<ClientConfig>(`${environment.glassApiUrl}/v2/config`).pipe(pollAndCache(30_000));
    const userProfile$ = this.http
      .get<UserProfile>(`${environment.glassApiUrl}/v2/userProfile`)
      .pipe(pollAndCache(30_000));
    const authenticated$ = this.ss.authState$.pipe(
      map((n) => n.isAuthenticated ?? false),
      startWith(false),
      distinctUntilChanged(),
      shareReplay({ bufferSize: 1, refCount: true }),
    );

    this.CurrentConfig$ = combineLatest([authenticated$, config$]).pipe(
      mergeMap(([authN, clientConfig]) => {
        if (!authN) {
          const error = new Error('Unauthenticated');
          return throwError(() => error);
        }
        return of(clientConfig as ClientConfig);
      }),
      shareReplay({ bufferSize: 1, refCount: true, windowTime: 30_000 }),
    );
    this.CurrentProfile$ = combineLatest([authenticated$, userProfile$]).pipe(
      mergeMap(([authN, userProfile]) => {
        if (!authN) {
          const error = new Error('Unauthenticated');
          return throwError(() => error);
        }
        return of(userProfile as UserProfile);
      }),
      shareReplay({ bufferSize: 1, refCount: true, windowTime: 30_000 }),
    );
  }

  /**
   * Fetches a named config from the API
   * @param configName - The name of the config to fetch
   * @returns Observable of the config object
   */
  getConfig(configName: string): Observable<Config> {
    return this.http.get<Config>(`${environment.glassApiUrl}/v2/config/${configName}`).pipe(
      catchError(handleHttpError)
    );
  }

  /**
   * Updates a named config in the API
   * @param config - The config object to update
   * @returns Observable of the updated config object
   */
  updateConfig(config: Config): Observable<Config> {
    return this.http.put<Config>(`${environment.glassApiUrl}/v2/config/${config.configId}`, config).pipe(
      catchError(handleHttpError)
    );
  }
}
